import { Loader, Table } from '@mantine/core'
import { useEffect, useMemo, useState } from 'react'
import {
  ORDER_REQUISITION_DOC_TYPE_CODES,
  OVERVIEW_DOC_TYPE_SYSTEM,
} from '@/model/middleware-types'
import { useParams } from 'react-router-dom'
import { parsePatientChartSplat } from '../patientChartPath'
import { useMedplum } from '@medplum/react'
import { DocumentReference, RequestGroup, Resource } from '@medplum/fhirtypes'
import { showNotification } from '@mantine/notifications'
import { formatDateTime, formatPhone } from '@/utils'
import { PdfViewer } from '@/components/PdfViewer'
import { tiffToURL } from '@/utils/tiffToURL'
import { useScrollContext } from '@/context/ScrollContext'
import {
  useAuditDocumentsOpenReporting,
  useAuditRecordOpenReporting,
} from '@/pages/Patient/useAuditDocumentReporting'

import { logError } from '@/utils/errorUtils'
import { OrderFaxStatus } from './OrderFaxStatus'

type Props = {
  sourceResourceId?: string
}

/**
 * One TIFF attachment's conversion outcome. A FAILURE IS A STATE, not an absent success:
 * the previous shape was a plain url-list map, so a conversion that threw was
 * indistinguishable from one still running and the attachment rendered as an empty box
 * forever. Something that failed to render has to say so — a compliance reader looking at
 * an order needs to know the paperwork exists and could not be shown, not to conclude
 * there is none.
 */
type TiffPreview = { status: 'ready'; urls: string[] } | { status: 'failed' }

function revokeTiffPreviews(previews: Record<string, TiffPreview>): void {
  for (const preview of Object.values(previews)) {
    if (preview.status === 'ready') preview.urls.forEach((url) => URL.revokeObjectURL(url))
  }
}

export const Orders = ({ sourceResourceId }: Props) => {
  const params = useParams()
  const medplum = useMedplum()
  const { scrollToTop } = useScrollContext()

  const [resourceId, setResourceId] = useState<string | undefined>(sourceResourceId)
  const [resource, setResource] = useState<RequestGroup>()
  const [documents, setDocuments] = useState<DocumentReference[]>([])
  const [tiffPreviews, setTiffPreviews] = useState<Record<string, TiffPreview>>({})
  const [isNotFound, setIsNotFound] = useState(false)

  // QA round 3 — the ORDER open is reported as a record open, unconditionally: an order
  // with no attached paperwork (or none renderable) used to log nothing at all, because
  // round 1 keyed the event on DocumentReference attachments existing. The server reads
  // the RequestGroup and sections the event under orders.
  useAuditRecordOpenReporting(
    params.patientId,
    resourceId !== undefined ? { resourceType: 'RequestGroup', id: resourceId } : undefined,
  )
  // The inline-rendered attachments remain their own document opens (W7/M2): each is a
  // real document the user saw, titled and sectioned by the server from the resource.
  const documentIds = useMemo(
    () => documents.map((doc) => doc.id).filter((id): id is string => id !== undefined),
    [documents],
  )
  useAuditDocumentsOpenReporting(params.patientId, documentIds)

  useEffect(() => {
    if (sourceResourceId) {
      setResourceId(sourceResourceId)
      return
    }
    const parsedResourceId = parsePatientChartSplat(params['*']).resourceId

    if (parsedResourceId !== resourceId) {
      setResourceId(parsedResourceId)
    }
  }, [params, resourceId, sourceResourceId])

  useEffect(() => {
    scrollToTop()
  }, [resourceId, scrollToTop])

  useEffect(() => {
    if (resourceId) {
      medplum
        .search('RequestGroup', `_id=${resourceId}&_revinclude=DocumentReference:related`)
        .then((resources) => {
          const entries = resources.entry || []
          const neededResource = entries.find((entry) => entry.resource?.id === resourceId)
            ?.resource as RequestGroup | undefined

          const docRefs = entries
            .map((entry) => entry.resource as Resource)
            .filter((res) => res?.resourceType === 'DocumentReference')
            .map((res) => res)
            .filter((doc) =>
              doc.context?.related?.some((rel) => rel.reference === `RequestGroup/${resourceId}`),
            )

          if (neededResource) {
            setResource(neededResource)
            setDocuments(docRefs)
            setIsNotFound(false)
          }
        })
        .catch((e) => {
          setIsNotFound(true)
          logError('patient', 'Fetch order', e)
          showNotification({
            title: 'Error',
            message: 'Failed to fetch resource',
            color: 'red',
          })
        })
    }

    return () => {
      setResource(undefined)
      setDocuments([])
      setTiffPreviews((prev) => {
        revokeTiffPreviews(prev)
        return {}
      })
      setIsNotFound(false)
    }
  }, [medplum, resourceId])

  // PER ATTACHMENT, AND FAILURE-TOLERANT. Every conversion catches its own error, so one
  // unreadable TIFF can no longer reject the batch — which used to leave the whole map
  // unset (no attachment rendered at all, previous object URLs never revoked) with nothing
  // on screen to say why. This effect is also the one piece of async work that only the
  // TIFF path runs, and it must stay entirely separate from the audit reporting above:
  // whether the order was opened is not a question about whether its paperwork rendered.
  useEffect(() => {
    let isCancelled = false

    const conversions = documents.flatMap((doc) =>
      (doc.content || [])
        .map((content) => content.attachment)
        .filter((attachment) => !!attachment?.url && /tiff/i.test(attachment.contentType || ''))
        .map(async (attachment): Promise<[string, TiffPreview]> => {
          const url = attachment?.url as string
          try {
            return [url, { status: 'ready', urls: await tiffToURL(medplum, url) }]
          } catch (e) {
            logError('patient', 'Convert TIFF images', e)
            return [url, { status: 'failed' }]
          }
        }),
    )

    void Promise.all(conversions).then((entries) => {
      const next = Object.fromEntries(entries)
      if (isCancelled) {
        // Nothing will render these, so release them here rather than leaking a decoded
        // page per abandoned order.
        revokeTiffPreviews(next)
        return
      }
      setTiffPreviews((prev) => {
        revokeTiffPreviews(prev)
        return next
      })
    })

    return () => {
      isCancelled = true
    }
  }, [documents, medplum])

  const [failedPdfUrls, setFailedPdfUrls] = useState<Record<string, boolean>>({})

  const attachments = useMemo(() => {
    const items: {
      id: string
      url: string
      type?: string
      title?: string
      isRequisition: boolean
    }[] = []
    for (const doc of documents) {
      // What the order flow stamps on the PDF it generates. Anything else filed
      // against the order — an authorization, a scan — is a supporting document
      // and does not carry the order's details (Greptile !1614).
      const isRequisition = !!doc.type?.coding?.some(
        (coding) =>
          coding.system === OVERVIEW_DOC_TYPE_SYSTEM &&
          ORDER_REQUISITION_DOC_TYPE_CODES.some((code) => code === coding.code),
      )
      for (const content of doc.content || []) {
        const attachment = content.attachment
        if (attachment?.url) {
          items.push({
            id: `${doc.id || 'doc'}-${attachment.url}`,
            url: attachment.url,
            type: attachment.contentType,
            title: attachment.title || doc.description || 'Attachment',
            isRequisition,
          })
        }
      }
    }
    return items
  }, [documents])

  // The requisition itself, if one is attached AND readable: it spells the order
  // out in full, which is what makes the bare-code tables redundant (PD-1220).
  // A PDF whose Binary can't be fetched shows an error in its place, so it can't
  // stand in for them — this repo has had exactly that failure (Greptile !1606).
  const hasRequisitionPdf = attachments.some(
    (attachment) =>
      attachment.isRequisition && attachment.type?.match(/pdf/i) && !failedPdfUrls[attachment.url],
  )

  if (!resourceId || isNotFound) {
    return null
  }

  if (!resource) {
    return (
      <div
        style={{ width: 'calc(100% - 560px - var(--zoom-panel-width, 0px))' }}
        className="fixed left-[560px] flex h-full items-center justify-center gap-3 text-sm text-[#7D7987]"
      >
        <Loader size="sm" />
        Loading order...
      </div>
    )
  }

  const getPerformer = () => {
    const ext = resource.extension?.find((item) => item.url.match(/requestgroup-performer/))

    return ext?.valueReference?.display || '-'
  }

  const getPlaced = () => {
    const ext = resource.extension?.find((item) => item.url.match(/requestgroup-deliveryOptions/))

    let subExt = ext?.extension?.find((item) => item.url.match(/electronic/))
    if (subExt && subExt?.valueBoolean) {
      return 'Electronically'
    }

    subExt = ext?.extension?.find((item) => item.url.match(/method/))
    if (subExt && subExt?.valueContactPoint) {
      return (
        subExt?.valueContactPoint?.system +
        ' ' +
        formatPhone(subExt?.valueContactPoint?.value ?? '')
      )
    }

    return '-'
  }

  const getLabReferenceId = () => {
    const identifier = resource.identifier?.find((item) =>
      item.type?.coding?.find((c) => c.code === 'ACSN'),
    )

    return identifier?.value || '-'
  }

  const getCreatedBy = () => {
    const ext = resource.extension?.find((item) => item.url.match(/requestgroup-authorizedBy/))

    const author = resource.author?.display || '-'

    return ext?.valueReference?.display ? `${author} (${ext?.valueReference?.display})` : author
  }

  return (
    <div className="relative box-border p-2 whitespace-pre-wrap">
      {/*<div className='whitespace-pre-wrap'>{JSON.stringify(resource, null, 2)}</div>*/}
      <div className="w-full bg-white pt-2">
        <p className="mb-4 text-2xl font-bold">Order Summary</p>
        <div className="flex justify-start gap-28">
          <div className="flex items-start gap-4">
            <div className="flex flex-col gap-2">
              <p className="text-sm font-bold text-[#7D7987]">Provider Name:</p>
              <p className="text-sm font-bold text-[#7D7987]">Status:</p>
              <p className="text-sm font-bold text-[#7D7987]">Order Placed:</p>
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-base text-sm">{getPerformer()}</p>
              <p className="text-base text-sm">{resource.status}</p>
              <p className="text-base text-sm">{getPlaced()}</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="flex flex-col gap-2">
              <p className="text-sm font-bold text-[#7D7987]">Lab Reference ID:</p>
              <p className="text-sm font-bold text-[#7D7987]">Order Created:</p>
              <p className="text-sm font-bold text-[#7D7987]">Order Created By:</p>
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-base text-sm">{getLabReferenceId()}</p>
              <p className="text-base text-sm">
                {!!resource.authoredOn && formatDateTime(resource.authoredOn, '-', true)}
                {!resource.authoredOn &&
                  formatDateTime(resource.meta?.lastUpdated || '-', '-', true)}
              </p>
              <p className="text-base text-sm">{getCreatedBy()}</p>
            </div>
          </div>
        </div>

        {resource.id && (
          <div className="mt-4">
            <OrderFaxStatus requestGroupId={resource.id} />
          </div>
        )}
      </div>

      <div className="my-4 h-[1px] bg-gray-200" />

      {/* The ordered tests and the diagnoses are listed as bare codes ONLY when no
          requisition PDF is attached. The PDF carries both with the test names and
          the ICD descriptions attached, so alongside it the lists said less than
          the document they sat on top of (PD-1220) — but without it they are the
          only record of what was ordered and why (Greptile !1606). */}
      {!hasRequisitionPdf && !!resource.reasonCode?.length && (
        <div className="mb-10">
          <p className="mb-2 text-xl">Diagnoses</p>

          <Table striped>
            <Table.Tbody>
              {resource.reasonCode.map((reason, index) => (
                <Table.Tr key={index}>
                  <Table.Td>{reason.coding?.[0]?.display || reason.text || '-'}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      )}

      {!hasRequisitionPdf && !!resource.action?.length && (
        <div className="mb-10">
          <p className="mb-2 text-xl">Tests / Procedures / Services</p>

          <Table striped>
            <Table.Tbody>
              {resource.action.map((action, index) => (
                <Table.Tr key={index}>
                  <Table.Td>{action.resource?.display || '-'}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      )}

      {!!attachments.length && (
        <div className="mb-10">
          <p className="mb-2 text-xl">Attachments</p>
          <div className="flex flex-col gap-4">
            {attachments.map((attachment) => {
              if (attachment.type?.match(/pdf/i)) {
                return (
                  <div key={attachment.id} className="border border-gray-200 bg-white p-2">
                    <p className="mb-2 text-sm text-gray-700">{attachment.title}</p>
                    <PdfViewer
                      fileUrl={attachment.url}
                      onLoadFailed={(failed) =>
                        setFailedPdfUrls((previous) =>
                          previous[attachment.url] === failed
                            ? previous
                            : { ...previous, [attachment.url]: failed },
                        )
                      }
                    />
                  </div>
                )
              }

              if (attachment.type?.match(/tiff/i)) {
                const preview = tiffPreviews[attachment.url]
                const tiffUrls = preview?.status === 'ready' ? preview.urls : []
                return (
                  <div key={attachment.id} className="border border-gray-200 bg-white p-2">
                    <p className="mb-2 text-sm text-gray-700">{attachment.title}</p>
                    {/* Every outcome says which one it is. Same wording as the Labs/Imaging
                        TIFF preview, so the two tabs do not describe one condition two
                        ways. */}
                    {preview === undefined && (
                      <p className="text-sm text-gray-500">Converting TIFF attachment...</p>
                    )}
                    {preview?.status === 'failed' && (
                      <p className="text-sm text-red-500">
                        Failed to load TIFF attachment.{' '}
                        <a
                          className="text-blue-600 underline"
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open attachment
                        </a>
                      </p>
                    )}
                    {preview?.status === 'ready' && tiffUrls.length === 0 && (
                      <p className="text-sm text-gray-500">No previewable TIFF pages found.</p>
                    )}
                    <div className="flex flex-col gap-3">
                      {tiffUrls.map((url) => (
                        <img
                          key={url}
                          src={url}
                          alt={attachment.title}
                          className="max-w-full border border-gray-100"
                        />
                      ))}
                    </div>
                  </div>
                )
              }

              if (attachment.type?.match(/image/i)) {
                return (
                  <div key={attachment.id} className="border border-gray-200 bg-white p-2">
                    <p className="mb-2 text-sm text-gray-700">{attachment.title}</p>
                    <img
                      src={attachment.url}
                      alt={attachment.title}
                      className="max-w-full border border-gray-100"
                    />
                  </div>
                )
              }

              return (
                <div key={attachment.id} className="border border-gray-200 bg-white p-2">
                  <p className="mb-2 text-sm text-gray-700">{attachment.title}</p>
                  <a
                    className="text-blue-600 underline"
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open attachment
                  </a>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
